import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { articles } from "../articlesData";
import useReveal from "../hooks/useReveal";
import { Heart, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MostLoved = () => {
  const [ref, visible] = useReveal();
  const [counts, setCounts] = useState({});

  useEffect(() => {
    const slugs = articles.map((a) => a.slug).join(",");
    if (!slugs) return;
    axios
      .get(`${API}/articles/meta-batch?slugs=${encodeURIComponent(slugs)}`)
      .then((res) => setCounts(res.data || {}))
      .catch(() => {});
  }, []);

  const ranked = [...articles]
    .map((a, i) => ({ a, i, hearts: (counts[a.slug] && counts[a.slug].reactions) || 0 }))
    .sort((x, y) => y.hearts - x.hearts || x.i - y.i)
    .slice(0, 4)
    .map((r) => ({ ...r.a, hearts: r.hearts }));

  if (ranked.length === 0) return null;

  return (
    <section className="bg-[#eceae5] pt-14 md:pt-20 pb-6 md:pb-8" data-testid="most-loved-section">
      <div
        ref={ref}
        className={`max-w-[1400px] mx-auto px-6 md:px-10 reveal ${visible ? "is-visible" : ""}`}
      >
        <div className="flex items-end justify-between gap-6 mb-9 md:mb-12">
          <div>
            <p className="font-sans-ui uppercase tracking-[0.25em] text-sm text-neutral-500">
              Start here
            </p>
            <h2
              className="font-display text-3xl md:text-5xl text-neutral-900 mt-3 leading-none tracking-tight"
              style={{ fontWeight: 700 }}
            >
              Most loved
            </h2>
          </div>
          <Link
            to="/topics"
            className="hidden sm:inline-flex items-center gap-2 font-sans-ui text-sm text-neutral-900 hover:opacity-70 transition-opacity shrink-0"
            data-testid="most-loved-browse-all"
          >
            Browse all <ArrowRight size={15} />
          </Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-7">
          {ranked.map((post, idx) => (
            <Link
              key={post.slug}
              to={`/article/${post.slug}`}
              className="group"
              data-testid={`most-loved-${post.slug}`}
            >
              <div className="relative w-full aspect-[4/5] overflow-hidden rounded-lg">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-105"
                />
                <span
                  className="absolute top-3 left-3 font-display text-white text-lg w-9 h-9 rounded-full bg-neutral-900/70 backdrop-blur-sm flex items-center justify-center"
                  style={{ fontWeight: 700 }}
                >
                  {idx + 1}
                </span>
                {post.hearts > 0 && (
                  <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 font-sans-ui text-xs text-white bg-neutral-900/70 backdrop-blur-sm px-2.5 py-1 rounded-full">
                    <Heart size={12} fill="currentColor" /> {post.hearts}
                  </span>
                )}
              </div>
              <p className="font-sans-ui text-[11px] uppercase tracking-widest text-neutral-500 mt-4">
                {post.category.charAt(0) + post.category.slice(1).toLowerCase()} &middot; {post.readTime}
              </p>
              <h3
                className="font-display text-lg md:text-xl text-neutral-900 mt-1.5 leading-snug group-hover:opacity-70 transition-opacity"
                style={{ fontWeight: 700 }}
              >
                {post.title}
              </h3>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MostLoved;
